import React, { useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Provider, useSelector } from "react-redux";
import { AuthProvider } from "./contexts/AuthContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { store } from "./store/store";
import { selectCurrentUser, selectIsAdmin } from "./store/slices/authSlice";
import { Box } from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useEffect } from "react";
import { initClarity, identifyClarityUser } from "./utils/clarity";

// Components
import {
  ProtectedRoute,
  Navbar,
  Login,
  // AdminDashboard,
  Footer,
} from "./components";

// Pages
import {
  Home,
  ContentManagement,
  Profile,
  Unauthorized,
  AdminDashboard,
  ExamSession,
  AssignmentManagement,
  ExamAttemptsManagement,
  MyExaminations,
  Memories,
  AdminRoute,
} from "./pages";
import {
  PublicMockTestList,
  PublicMockTestBookDetail,
  PublicMockTestRunner,
} from "./pages/user";
import VocabularyLookupProvider from "./components/VocabularyLookup";
import { HomepageManagement } from "./pages/admin";
import ExerciseManagement from "./pages/admin/ExerciseManagement";
import MyExercises from "./pages/user/Exercises";
// import ExerciseDetail from "./pages/user/Exercises/ExerciseDetail";
// import ExerciseDay from "./pages/user/Exercises/ExerciseDay";

// Layouts
import UserLayout from "./layouts/UserLayout";
import MyStatistics from "./pages/user/MyStatistics";
import {
  buildStatisticsPath,
  STATISTICS_TABS,
} from "./pages/user/MyStatistics/constants";
import AdminReports from "./pages/admin/Reports";
import { ContentPreviewDialog } from "./components/common";
import ExerciseDetail from "./pages/user/Exercises/ExerciseDetail";
import TrialLaboratory from "./pages/user/TrialLaboratory";
import TrialLabTest from "./pages/user/TrialLaboratory/TrialLabTest";
// import ExerciseSession from "./pages/user/ExerciseSession";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

// Main layout component that wraps all pages
const AppLayout = ({ children, isAdmin = false }) => {
  const currentUser = useSelector(selectCurrentUser);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        {!isAdmin ? (
          <UserLayout>{children}</UserLayout>
        ) : (
          children
        )}
        <ToastContainer />
        <ContentPreviewDialog />
      </Box>
      <Footer />
    </Box>
  );
};

// Main App Content with Routes
const AppContent = () => {
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectIsAdmin);

  // Load Clarity once at app boot (no-op without REACT_APP_CLARITY_ID
  // or outside production — see src/utils/clarity.js).
  useEffect(() => {
    initClarity();
  }, []);

  // Whenever the logged-in user changes, tell Clarity who they are so
  // their session replay is searchable by username/email/role in the
  // dashboard. Critical for ticket triage.
  useEffect(() => {
    // Different code paths surface the id as either `_id` (Mongo raw) or
    // `id` (serialized) — accept either so we don't silently no-op.
    const uid = currentUser?._id || currentUser?.id;
    if (uid) {
      identifyClarityUser(currentUser);
    }
  }, [currentUser?._id, currentUser?.id, currentUser?.username, currentUser?.role]);

  // For admin users, we don't want to use the UserLayout
  const renderWithLayout = (Component, adminRoute = false) => {
    if (adminRoute) {
      return (
        <AdminRoute>
          <AppLayout isAdmin>
            <Component />
          </AppLayout>
        </AdminRoute>
      );
    }

    return (
      <ProtectedRoute>
        <AppLayout>
          <Component />
        </AppLayout>
      </ProtectedRoute>
    );
  };

  const renderPublicWithLayout = (Component) => {
    return (
      <AppLayout>
        <Component />
      </AppLayout>
    );
  };

  return (
    <VocabularyLookupProvider>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={currentUser ? <Navigate to="/" /> : <Login />}
      />

      {/* Public Routes */}
      <Route path="/" element={renderPublicWithLayout(Home)} />

      {/* Public Mock Tests — accessible to guests and logged-in users */}
      <Route
        path="/mock-tests"
        element={renderPublicWithLayout(PublicMockTestList)}
      />
      <Route
        path="/mock-tests/list"
        element={<Navigate to="/mock-tests" replace />}
      />
      <Route
        path="/mock-tests/books/:bookId"
        element={renderPublicWithLayout(PublicMockTestBookDetail)}
      />
      <Route
        path="/mock-tests/:examId/run"
        element={renderPublicWithLayout(PublicMockTestRunner)}
      />
      <Route
        path="/mock-tests/history"
        element={<Navigate to={buildStatisticsPath(STATISTICS_TABS.PUBLIC)} replace />}
      />

      {/* Protected User Routes */}
      <Route path="/profile" element={renderWithLayout(Profile)} />
      <Route
        path="/vocabulary"
        element={renderWithLayout(() => (
          <div>Vocabulary Page</div>
        ))}
      />
      <Route
        path="/my-exercises"
        element={renderWithLayout(MyExercises)}
      />
      <Route
        path="/exercise/:exerciseId"
        element={renderWithLayout(ExerciseDetail)}
      />
      <Route
        path="/my-assignments"
        element={renderWithLayout(() => (
          <div>My Assignments Page</div>
        ))}
      />
      <Route
        path="/exam-session/:examId"
        element={renderWithLayout(() => (
          <ExamSession />
        ))}
      />
      <Route
        path="/statistics"
        element={renderWithLayout(MyStatistics)}
      />
      <Route
        path="/my-examinations"
        element={renderWithLayout(MyExaminations)}
      />
      <Route
        path="/memories"
        element={renderWithLayout(Memories)}
      />
      <Route
        path="/trial-laboratory"
        element={renderWithLayout(TrialLaboratory)}
      />
      <Route
        path="/trial-laboratory/test/:testIndex"
        element={renderWithLayout(TrialLabTest)}
      />

      {/* Admin Routes */}
      <Route path="/admin" element={renderWithLayout(AdminDashboard, true)} />
      <Route
        path="/admin/content"
        element={renderWithLayout(ContentManagement, true)}
      />
      <Route
        path="/admin/homepage"
        element={renderWithLayout(HomepageManagement, true)}
      />
      <Route
        path="/admin/exam-assignments"
        element={renderWithLayout(AssignmentManagement, true)}
      />
      <Route
        path="/admin/exam-attempts"
        element={renderWithLayout(ExamAttemptsManagement, true)}
      />
      <Route
        path="/admin/exercises"
        element={renderWithLayout(ExerciseManagement, true)}
      />
      <Route
        path="/admin/reports"
        element={renderWithLayout(AdminReports, true)}
      />

      {/* Other Routes */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </VocabularyLookupProvider>
  );
};

// Main App Component
const App = () => {
  const router = createBrowserRouter(
    [
      {
        path: "/*",
        element: (
          <Provider store={store}>
            <AuthProvider>
              <SidebarProvider>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <AppContent />
                </ThemeProvider>
              </SidebarProvider>
            </AuthProvider>
          </Provider>
        ),
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
      },
    }
  );

  return <RouterProvider router={router} />;
};

export default App;
